import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Card,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { AdminPanelSettings, Close, Refresh, Search } from '@mui/icons-material';
import { format } from 'date-fns';
import { fetchAdminUsers, fetchAdminUserDetail } from '../hooks/hooks';

const KYC_CHIP_COLOR = {
  approved: 'success',
  pending: 'warning',
  rejected: 'error',
  not_submitted: 'default',
};

const fmtDate = (val) => (val ? format(new Date(val), 'MMM d, yyyy') : '—');
const fmtDateTime = (val) => (val ? format(new Date(val), 'MMM d, yyyy HH:mm') : '—');

const DetailRow = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography fontSize={11} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
      {label}
    </Typography>
    <Typography fontSize={14} fontWeight={500}>
      {value === null || value === undefined || value === '' ? '—' : String(value)}
    </Typography>
  </Grid>
);

function UserDetailDialog({ userId, onClose }) {
  const { data: user, isLoading, error } = useQuery({
    queryKey: ['adminUserDetail', userId],
    queryFn: () => fetchAdminUserDetail(userId),
    enabled: !!userId,
  });

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontWeight={700} fontSize={16}>
            User Details
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 2.5 }}>
        {isLoading && <Typography color="text.secondary">Loading...</Typography>}
        {error && <Alert severity="error">Failed to load user details.</Alert>}
        {user && (
          <Grid container spacing={2}>
            <DetailRow label="Account ID" value={user.account_id} />
            <DetailRow label="Username" value={user.username} />
            <DetailRow label="Email" value={user.email} />
            <DetailRow label="Phone" value={user.phone} />
            <DetailRow label="First Name" value={user.first_name} />
            <DetailRow label="Last Name" value={user.last_name} />
            <DetailRow label="Gender" value={user.gender} />
            <DetailRow label="Date of Birth" value={fmtDate(user.date_of_birth)} />
            <DetailRow label="Country" value={user.country} />
            <DetailRow label="Zip Code" value={user.zip_code} />
            <DetailRow label="User Type" value={user.user_type} />
            <DetailRow label="Business Name" value={user.business_name} />
            <DetailRow label="Business Reg. Number" value={user.business_registration_number} />
            <DetailRow label="Tax ID" value={user.tax_id} />
            <DetailRow label="Address" value={user.address} />
            <DetailRow label="KYC Status" value={(user.kyc_review_status || '').replace('_', ' ')} />
            <DetailRow label="ID Type" value={user.id_type} />
            <DetailRow label="ID Number" value={user.id_number} />
            <DetailRow label="NIDA ID" value={user.nida_id} />
            <DetailRow label="NIDA Verified" value={user.nida_verified ? 'Yes' : 'No'} />
            <DetailRow label="Email Verified" value={user.email_verified ? 'Yes' : 'No'} />
            <DetailRow label="Active" value={user.is_active ? 'Yes' : 'No'} />
            <DetailRow label="Staff" value={user.is_staff ? 'Yes' : 'No'} />
            <DetailRow label="Referral Code" value={user.referral_code} />
            <DetailRow label="Referral Balance" value={user.referral_balance} />
            <DetailRow label="Date Joined" value={fmtDateTime(user.date_joined)} />
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
}

const AdminUsers = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const borderClr = isDark ? 'rgba(255,255,255,0.09)' : '#E2E8F0';

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const {
    data: users,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['adminUsersList'],
    queryFn: fetchAdminUsers,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const filteredUsers = useMemo(() => {
    const userList = Array.isArray(users) ? users : [];
    if (!search) return userList;
    const q = search.toLowerCase();
    return userList.filter((u) =>
      [u.username, u.email, u.business_name, u.phone, u.account_id, u.country]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q))
    );
  }, [users, search]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
            <AdminPanelSettings sx={{ color: '#0BA480', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>
              All Users
            </Typography>
            <Chip label="Admin" size="small" color="success" sx={{ fontWeight: 600 }} />
          </Stack>
          <Typography color="text.secondary" fontSize={14}>
            Browse all platform users, search, and view full profile details.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={() => refetch()}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
          Failed to load users. Please try refreshing.
        </Alert>
      )}

      <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px' }}>
        <Box sx={{ px: 2, pt: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>
              Users {isLoading ? '' : `(${filteredUsers.length})`}
            </Typography>
            <TextField
              size="small"
              placeholder="Search users..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <Search sx={{ fontSize: 18, mr: 0.75, color: 'text.disabled' }} /> }}
              sx={{ minWidth: 240 }}
            />
          </Stack>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Account ID</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Username</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Business</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Country</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Gender</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>KYC</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Joined</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">Loading users...</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((u) => (
                    <TableRow
                      key={u.user_id}
                      hover
                      onClick={() => setSelectedUserId(u.user_id)}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'action.hover' } }}
                    >
                      <TableCell>
                        <Typography fontFamily="monospace" fontSize={12.5}>{u.account_id}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13} fontWeight={500}>{u.username}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13}>{u.business_name || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13}>{u.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13}>{u.phone || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13}>{u.country || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13}>{u.gender || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13} sx={{ textTransform: 'capitalize' }}>{u.user_type}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(u.kyc_review_status || 'not_submitted').replace('_', ' ')}
                          size="small"
                          color={KYC_CHIP_COLOR[u.kyc_review_status] || 'default'}
                          sx={{ fontSize: 11, textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={u.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={u.is_active ? 'success' : 'default'}
                          sx={{ fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={12.5}>{fmtDate(u.date_joined)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredUsers.length > 0 && (
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        )}
      </Card>

      {selectedUserId && (
        <UserDetailDialog userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
      )}
    </Container>
  );
};

export default AdminUsers;
